import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Lightbulb, Quote } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { cms } from '../i18n/localize';

interface StudentStory {
  id?: string | number;
  name: string;
  jobRole?: string;
  storyText: string;
  quote?: string;
  photoUrl?: string;
  illustrationUrl?: string;
}

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const StudentStoriesSection: React.FC = () => {
  const { t } = useTranslation();
  const [stories, setStories] = useState<StudentStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [flippedIds, setFlippedIds] = useState<Set<string | number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    const fetchStories = async () => {
      try {
        const res = await apiFetch('/api/public/stories');
        if (!res.ok) throw new Error('Failed to fetch stories');
        const json = await res.json();
        if (cancelled) return;
        const list: StudentStory[] = Array.isArray(json) ? json : json.data ?? json.stories ?? [];
        setStories(list.slice(0, 3));
      } catch {
        if (cancelled) return;
        setStories([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStories();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleFlip = useCallback((id: string | number = Math.random()) => {
    setFlippedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

    setTimeout(() => {
      setFlippedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 5000);
  }, []);

  if (loading) {
    return (
      <section className="py-16 md:py-24 px-4 bg-[#FFF7ED]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl overflow-hidden border border-stone-100 animate-pulse"
              >
                <div className="aspect-square bg-stone-200" />
                <div className="p-6 space-y-3">
                  <div className="h-5 bg-stone-200 rounded w-1/2" />
                  <div className="h-4 bg-stone-200 rounded w-1/3" />
                  <div className="h-4 bg-stone-200 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (stories.length === 0) {
    return (
      <section className="py-16 md:py-24 px-4 bg-[#FFF7ED]">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
          >
            <span className="inline-block px-4 py-1.5 bg-white text-[#C2410C] text-sm font-bold rounded-full mb-4">
              {t('content:stories.badge')}
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-4">{t('content:stories.heading')}</h2>
            <p className="text-stone-500">{t('content:stories.empty')}</p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 px-4 bg-[#FFF7ED]">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeInUp}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="inline-block px-4 py-1.5 bg-white text-[#C2410C] text-sm font-bold rounded-full mb-4">
            {t('content:stories.badge')}
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-4">{t('content:stories.heading')}</h2>
          <p className="text-stone-600 leading-relaxed">
            {t('content:stories.intro')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stories.map((story, idx) => {
            const id = story.id ?? idx;
            const isFlipped = flippedIds.has(id);
            const hasIllustration = Boolean(story.illustrationUrl);

            return (
              <motion.div
                key={id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-80px' }}
                variants={{
                  hidden: { opacity: 0, y: 24 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.6, delay: idx * 0.1, ease: 'easeOut' },
                  },
                }}
                className="bg-white rounded-2xl overflow-hidden border border-stone-100 hover:shadow-lg transition-shadow"
              >
                <div className="relative aspect-square bg-stone-100">
                  <AnimatePresence mode="wait">
                    {isFlipped && hasIllustration ? (
                      <motion.img
                        key="illustration"
                        src={story.illustrationUrl}
                        alt={t('content:stories.inspirationAlt', { name: story.name })}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <motion.img
                        key="photo"
                        src={story.photoUrl}
                        alt={story.name}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                  </AnimatePresence>

                  {hasIllustration && (
                    <button
                      onClick={() => toggleFlip(id)}
                      className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/90 backdrop-blur-sm text-[#C2410C] text-xs font-bold rounded-full shadow-sm hover:bg-white transition-colors"
                      aria-label={t('content:stories.inspirationAria')}
                    >
                      <Lightbulb size={14} />
                      {t('content:stories.inspirationBtn')}
                    </button>
                  )}
                </div>

                <div className="p-6">
                  <h3 className="text-lg font-bold text-stone-800 mb-1">{cms(`storyItems.${id}.name`, story.name)}</h3>
                  {story.jobRole && (
                    <p className="text-sm text-[#C2410C] font-medium mb-3">{cms(`storyItems.${id}.jobRole`, story.jobRole)}</p>
                  )}
                  <p className="text-stone-600 text-sm leading-relaxed line-clamp-4 mb-4">
                    {cms(`storyItems.${id}.storyText`, story.storyText)}
                  </p>
                  {story.quote && (
                    <div className="flex items-start gap-2 pt-4 border-t border-stone-100">
                      <Quote size={16} className="text-stone-400 flex-shrink-0 mt-0.5" />
                      <p className="text-stone-500 text-sm italic leading-relaxed">
                        「{cms(`storyItems.${id}.quote`, story.quote)}」
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StudentStoriesSection;
